import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import Dashboard from './Dashboard';

/**
 * Composant qui charge le dashboard approprié selon le rôle de l'utilisateur
 * Utilise les templates de dashboard configurés dans Firestore
 */
const RoleBasedDashboard = ({ db, currentUser, ...props }) => {
  const [dashboardTemplate, setDashboardTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadDashboardTemplate = async () => {
      if (!db || !currentUser?.role) {
        setLoading(false);
        return;
      }

      try {
        // 1. Essayer de charger le template spécifique au rôle
        const templateDoc = await getDoc(doc(db, 'dashboard_templates', currentUser.role));
        
        if (templateDoc.exists()) {
          const templateData = templateDoc.data();
          setDashboardTemplate(templateData);
        } else {
          // 2. Fallback: utiliser le template par défaut
          const defaultTemplate = await getDoc(doc(db, 'dashboard_templates', 'default'));
          
          if (defaultTemplate.exists()) {
            setDashboardTemplate(defaultTemplate.data());
          } else {
            // 3. Fallback ultime: template hardcodé selon le rôle
            setDashboardTemplate(getHardcodedTemplate(currentUser.role));
          }
        }
      } catch (error) {
        setError(error.message);
        // En cas d'erreur, utiliser le template hardcodé
        setDashboardTemplate(getHardcodedTemplate(currentUser.role));
      } finally {
        setLoading(false);
      }
    };

    loadDashboardTemplate();
  }, [db, currentUser]);

  // Templates hardcodés en fallback
  const getHardcodedTemplate = (role) => {
    const baseTiles = {
      dateTime: true,
      monEspace: true,
      vueEnsemble: false,
      prochainRendezVous: true,
      gestionCollaborateurs: false,
      suiviPresences: false,
      sommairePaie: true,
      anniversaires: true,
      suiviDocuments: false,
      suiviTaches: false,
      suiviFactures: false,
      suiviDevis: false,
      suiviContacts: false
    };

    switch (role) {
      case 'admin':
      case 'super_admin':
        return {
          role: role,
          name: `Dashboard ${role === 'super_admin' ? 'Super Admin' : 'Administrateur'}`,
          tiles: {
            ...baseTiles,
            vueEnsemble: true,
            gestionCollaborateurs: true,
            suiviPresences: true,
            suiviDocuments: true,
            suiviTaches: true,
            suiviFactures: true,
            suiviDevis: true,
            suiviContacts: true
          },
          defaultView: 'dashboard'
        };
      
      case 'manager':
      case 'coordinateur':
        return {
          role: role,
          name: 'Dashboard Coordinateur',
          tiles: {
            ...baseTiles,
            vueEnsemble: true,
            gestionCollaborateurs: true,
            suiviPresences: true,
            suiviDocuments: true,
            suiviTaches: true
          },
          defaultView: 'dashboard'
        };
      
      case 'intervenant':
      case 'employee':
      default:
        return {
          role: role,
          name: 'Dashboard Intervenant',
          tiles: baseTiles,
          defaultView: 'dashboard'
        };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement du dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center p-6 bg-red-50 rounded-lg">
          <h3 className="text-lg font-semibold text-red-800 mb-2">Erreur de chargement</h3>
          <p className="text-red-600">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  // Passer les préférences de tuiles au composant Dashboard
  return (
    <Dashboard 
      db={db} 
      currentUser={currentUser} 
      roleBasedTiles={dashboardTemplate?.tiles}
      {...props}
    />
  );
};

export default RoleBasedDashboard;
